/** What a card holder row means for support: the one cards run on, a dead attempt, or one still in progress. */
export type HolderRole = "active" | "old" | "in_progress"

export interface HolderLike {
	status?: unknown
	kyc_status?: unknown
	provider_holder_id?: unknown
	cards_count?: unknown
}

const has = (value: unknown) => value !== null && value !== undefined && String(value).trim() !== ""

export const holderRole = (holder: HolderLike): HolderRole => {
	if (holder.status === "approved" && has(holder.provider_holder_id)) return "active"
	// Never reached the issuer and nothing was issued on it: a leftover from an earlier try.
	if (!has(holder.provider_holder_id) && !Number(holder.cards_count)) return "old"
	return "in_progress"
}

/** Role-aware locks from system/user/overview (Superadmin_User_KYC_FE.md §2); older backends omit it. */
export interface UserFlowLike { cards_scope?: "own" | "none"; cards_note?: string | null; blocking?: string[]; next_action?: string }

/** One-line answer to "can this client use cards right now, and if not, why". `neutral`: cards are not for this role, nothing is wrong. */
export const kycVerdict = (ourKyc: string | undefined, holders: HolderLike[], userFlow?: UserFlowLike): { ok: boolean; neutral?: boolean; text: string } => {
	// Older backends send `blocking` with every lock for everyone; only a flow that knows `cards_scope` is role-aware.
	const flow = userFlow?.cards_scope ? userFlow : undefined
	if (flow?.cards_scope === "none") return { ok: true, neutral: true, text: flow.cards_note || "No cards for this role — the company's buyers hold the cards." }
	if (flow?.blocking?.length) return { ok: false, text: `Blocked: ${flow.next_action || flow.blocking[0]}` }
	const active = holders.filter((holder) => holderRole(holder) === "active").length
	if (flow?.blocking) return { ok: true, text: active ? `Client can use cards: ${active} approved card holder${active === 1 ? "" : "s"} at the issuer.` : "Nothing is blocking this user." }
	if (ourKyc !== "approved") return { ok: false, text: `Blocked: our KYC is ${ourKyc ? ourKyc.replaceAll("_", " ") : "not started"}.` }
	if (active) return { ok: true, text: `Client can use cards: ${active} approved card holder${active === 1 ? "" : "s"} at the issuer.` }
	if (holders.some((holder) => holderRole(holder) === "in_progress")) return { ok: false, text: "Not ready yet: the card holder is still being registered with the issuer." }
	return { ok: false, text: "Blocked: no card holder is registered with the issuer yet." }
}
